import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import { AppState } from 'redux/config/store'
import { Locations } from 'shared/Router/Locations'
import { FinchEmploymentTypes, BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import GridCard from 'employee-platform/shared/components/GridCard'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import palette from 'core-system/Themes/palette'
import Checkmark from 'core-system/Icons/Actions/Checkmark'
import Copy from 'core-system/Icons/Actions/Copy'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import Coupon from 'core-system/Icons/Programs/Coupon'
import {
  Flex as FlexIcon,
  Micromobility as MicromobilityIcon,
} from 'core-system/Icons/Sidebar'
import { Car as CarIcon } from 'core-system/Icons/modeTypes'
import { OneTimePurchase as OTPIcon } from 'core-system/Icons/Micromobility'
import RandomUtils from 'shared/RandomUtils'
import { CurrencyType } from 'features/Programs/Shared/types'
import { getProgramDisplayName } from 'employee-platform/shared/utils'
import {
  MyProgramRow,
  myProgramRows,
  unenrolledTransitPasses,
} from '../dashboardSummary'

const Summary = styled(Text)`
  white-space: nowrap
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  & + & {
    margin-top: 0.75rem;
  }
`

const GroupLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: ${(props) => props.theme.palette.success.green700};
  letter-spacing: 0.04em;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey200};
  border-radius: 0.625rem;
`

const IconSquare = styled.div<{ $bg: string }>`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: ${(props) => props.$bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const CopyBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.palette.grey.grey200};
  color: ${(props) => props.theme.palette.text.primary};
  font-size: 0.75rem;
  line-height: 1.4;
`

const TagIcon = styled.img`
  width: 0.875rem;
  height: 0.875rem;
  object-fit: contain;
`

const OfferSection = styled.div`
  margin-top: 1rem;

  &:first-child {
    margin-top: 0;
  }
`

const OfferHeading = styled.div`
  margin-bottom: 0.5rem;
  letter-spacing: 0.04em;
`

const OfferGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 26rem), 26rem));
  justify-content: start;
  gap: 0.75rem;
`

const OfferCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey200};
  border-radius: 0.75rem;
  min-width: 0;
`

const OfferHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`

const OfferIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`

const OfferLogo = styled.img`
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
  flex-shrink: 0;
`

const AvailableBadge = styled.span`
  flex-shrink: 0;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  background: ${(props) => props.theme.palette.grey.grey100};
  color: ${(props) => props.theme.palette.success.green700};
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
`

const OfferFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: auto;
`

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

const Pill = styled.span<{ $preTax: boolean }>`
  flex-shrink: 0;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  background: ${(props) =>
    props.$preTax
      ? props.theme.palette.primary.pink100
      : props.theme.palette.accent.blue100};
  color: ${(props) =>
    props.$preTax
      ? props.theme.palette.primary.pink800
      : props.theme.palette.accent.blue800};
`

const CouponActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const iconFor = (type: MyProgramRow['type']) => {
  switch (type) {
    case 'QTF':
      return {
        bg: palette.primary.pink100,
        node: (
          <Text variant='action3' textColor={palette.primary.pink800}>
            $
          </Text>
        ),
      }
    case 'MICROMOBILITY':
      return {
        bg: palette.accent.blue100,
        node: (
          <MicromobilityIcon
            width={18}
            height={18}
            color={palette.accent.blue800}
          />
        ),
      }
    case 'GRH':
      return {
        bg: palette.secondary.purple100,
        node: (
          <CarIcon width={18} height={18} color={palette.secondary.purple600} />
        ),
      }
    case 'OTP':
      return {
        bg: palette.grey.grey100,
        node: <OTPIcon width={18} height={18} color={palette.grey.grey700} />,
      }
    case 'FLEX':
      return {
        bg: palette.secondary.purple100,
        node: (
          <FlexIcon
            width={18}
            height={18}
            color={palette.secondary.purple600}
          />
        ),
      }
    case 'TRANSIT_PASS':
      return {
        bg: palette.secondary.purple100,
        node: (
          <CreditCard
            width={18}
            height={18}
            color={palette.secondary.purple600}
          />
        ),
      }
    case 'COUPON':
      return {
        bg: palette.accent.teal100,
        node: <Coupon width={18} height={18} color={palette.accent.teal600} />,
      }
    default:
      return {
        bg: palette.grey.grey100,
        node: (
          <Text variant='action3' textColor={palette.text.secondary}>
            $
          </Text>
        ),
      }
  }
}

const managePath = (type: MyProgramRow['type']): string | null => {
  switch (type) {
    case 'QTF':
      return Locations.EmployeePreTax.Overview
    case 'GRH':
      return Locations.EmployeeGRH.Overview
    case 'FLEX':
      return Locations.EmployeeFlex.Overview
    case 'MICROMOBILITY':
      return Locations.EmployeeMicromobility.Overview
    case 'TRANSIT_PASS':
      return Locations.EmployeeTransitPass.Overview
    case 'REPORTED_USAGE':
      return Locations.EmployeeReportedUsage
    default:
      return null
  }
}

const ProgramRow = ({ row }: { row: MyProgramRow }) => {
  const navigate = useNavigate()
  const [revealed, setRevealed] = useState(false)
  const icon = iconFor(row.type)
  const manageTo = managePath(row.type)

  return (
    <Row>
      <IconSquare $bg={icon.bg}>{icon.node}</IconSquare>
      <CopyBlock>
        <Text variant='action2'>{row.name}</Text>
        <Text variant='body2' textColor={palette.text.secondary}>
          {row.detail}
        </Text>
        {row.tags.length > 0 ? (
          <Tags>
            {row.tags.map((tag) => (
              <Tag key={tag.id}>
                {tag.iconUrl ? <TagIcon src={tag.iconUrl} alt='' /> : null}
                {tag.label}
              </Tag>
            ))}
          </Tags>
        ) : null}
        {row.couponCode ? (
          <CouponActions>
            <Text variant='action3'>
              {revealed ? row.couponCode : '••••••••'}
            </Text>
            {revealed ? (
              <Button
                variant='tertiary'
                size='small'
                aria-label='Copy coupon code'
                onClick={() => RandomUtils.copyToClipboard(row.couponCode || '')}
              >
                <Copy width={16} height={16} />
              </Button>
            ) : null}
            <Button
              variant='secondary'
              size='small'
              onClick={() => setRevealed((open) => !open)}
            >
              {revealed ? 'Hide' : 'Reveal'}
            </Button>
          </CouponActions>
        ) : null}
      </CopyBlock>
      <RowActions>
        <Pill $preTax={row.pill === 'pre-tax'}>
          {row.pill === 'pre-tax' ? 'PRE-TAX' : 'EMPLOYER-PAID'}
        </Pill>
        {manageTo ? (
          <Button
            variant='secondary'
            size='small'
            onClick={() => navigate(manageTo)}
          >
            Manage
          </Button>
        ) : null}
      </RowActions>
    </Row>
  )
}

const ProgramGroup = ({
  label,
  rows,
  enrolled,
}: {
  label: string
  rows: MyProgramRow[]
  enrolled?: boolean
}) => {
  if (rows.length === 0) return null
  return (
    <Group>
      <GroupLabel>
        {enrolled ? (
          <Checkmark width={16} height={16} color={palette.success.green600} />
        ) : null}
        <Text
          variant='action4'
          textColor={enrolled ? palette.success.green700 : palette.text.secondary}
        >
          {label}
        </Text>
      </GroupLabel>
      {rows.map((row) => (
        <ProgramRow key={row.id} row={row} />
      ))}
    </Group>
  )
}

const TransitPassOffer = ({ program }: { program: BaseProgram }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const agency = program.transitAgencies?.find((item) => item.iconUrl?.trim())
  const logo = agency?.iconUrl?.trim()
  const category = agency?.name
    ? `${agency.name} transit pass`
    : 'Transit pass'

  return (
    <OfferCard>
      <OfferHeader>
        <OfferIdentity>
          {logo ? (
            <OfferLogo src={logo} alt={agency?.name || 'Transit pass'} />
          ) : (
            <CreditCard
              width={18}
              height={18}
              color={palette.secondary.purple600}
            />
          )}
          <Text variant='caption' textColor={palette.text.secondary}>
            {category.toUpperCase()}
          </Text>
        </OfferIdentity>
        <AvailableBadge>AVAILABLE</AvailableBadge>
      </OfferHeader>
      <Text variant='action2'>{getProgramDisplayName(program)}</Text>
      {program.description?.trim() ? (
        <Text variant='body2' textColor={palette.text.secondary}>
          {program.description.trim()}
        </Text>
      ) : null}
      <OfferFooter>
        <Pill $preTax={false}>EMPLOYER-PAID</Pill>
        <Button
          variant='secondary'
          size='small'
          onClick={() => {
            dispatch(employeePlatformActions.enrollTransitPass(program.id))
            navigate(Locations.EmployeeTransitPass.Overview)
          }}
        >
          Enroll
        </Button>
      </OfferFooter>
    </OfferCard>
  )
}

const MyProgramsCard = () => {
  const { programs, preTaxPrograms, profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const properties = useSelector(
    (state: AppState) => state.application.properties
  )
  const programList = programs?.allPrograms
    ? Object.values(programs.allPrograms).flatMap((typePrograms) =>
        Object.values(typePrograms)
      )
    : []
  const rows = myProgramRows({
    programs: programList,
    transit: preTaxPrograms?.COMMUTER_TRANSIT ?? null,
    parking: preTaxPrograms?.COMMUTER_PARKING ?? null,
    isContractor:
      profileData?.finchEmploymentType === FinchEmploymentTypes.CONTRACTOR,
    irsTaxLimit: properties?.irsTaxLimit ?? null,
    currency: (profileData?.worksiteCurrency as CurrencyType) || 'USD',
    conversionRates: properties,
  })
  const offers = unenrolledTransitPasses(programList)

  if (rows.length === 0 && offers.length === 0) return null

  const enrolled = rows.filter((row) => row.enrollment === 'enrolled')
  const available = rows.filter((row) => row.enrollment === 'available')

  return (
    <GridCard
      headerTitle='My Programs'
      headerButtons={
        <Summary variant='body2' textColor={palette.text.secondary}>
          {enrolled.length} enrolled · {available.length + offers.length} available
        </Summary>
      }
    >
      <ProgramGroup label='ENROLLED' rows={enrolled} enrolled />
      <ProgramGroup label='AVAILABLE' rows={available} />
      {offers.length > 0 ? (
        <OfferSection>
          <OfferHeading>
            <Text variant='caption' textColor={palette.text.secondary}>
              AVAILABLE TO ENROLL
            </Text>
          </OfferHeading>
          <OfferGrid>
            {offers.map((program) => (
              <TransitPassOffer key={program.id} program={program} />
            ))}
          </OfferGrid>
        </OfferSection>
      ) : null}
    </GridCard>
  )
}

export default MyProgramsCard
